import { z } from "zod";

// Limits: what an organization may use of Ravnar, and where it stands against them. The API, the functions that run
// Intake's agents and the frontend share these. The storage an organization's documents may take is Intake's own,
// `INTAKE_STORAGE_LIMIT` in `./intake.ts`. See `docs/limits.md`.

/**
 * The most AI tokens an organization's agents may spend in a month: 10,000,000, what the model is sent and what it
 * writes counted together, in a calendar month in UTC. Once an organization has spent them, a document waits at the
 * next step of Intake that would spend more, and goes on once a new month has begun
 * (`apps/backend/lib/api/usage/tokens.ts`).
 */
export const MONTHLY_AI_TOKEN_LIMIT = 10_000_000;

/** Where an organization stands against its limits: what `GET /api/organization/limits` answers any of its members. */
export const organizationLimitsSchema = z.object({
	storage: z.object({
		/** The bytes its Intake documents take, each counted once. */
		used: z.int().nonnegative(),
		/** The most they may take: `INTAKE_STORAGE_LIMIT`. */
		limit: z.int().positive(),
	}),
	aiTokens: z.object({
		/** The tokens its agents have spent this month, what they were sent and what they wrote together. */
		used: z.int().nonnegative(),
		/** The most they may spend in a month: `MONTHLY_AI_TOKEN_LIMIT`. */
		limit: z.int().positive(),
		/** When the month's count starts again at nothing: 00:00 UTC on the first of the next month. */
		resetsAt: z.iso.datetime(),
	}),
	/** How many of its documents wait for AI tokens, at whichever step; each counts once. */
	waitingDocuments: z.int().nonnegative(),
});
export type OrganizationLimits = z.infer<typeof organizationLimitsSchema>;
